import { Routes, Route, Link, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import PreviewOurStory from './PreviewOurStory';
import PreviewOurMethod from './PreviewOurMethod';
import PreviewHome from './PreviewHome';
import PreviewServices from './PreviewServices';
import PreviewTeam from './PreviewTeam';
import PreviewContact from './PreviewContact';
import ImagePicker from './ImagePicker';

const PreviewNavigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (path) => location.pathname === `/preview${path}`;

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-white shadow-lg py-4' : 'bg-white/95 backdrop-blur-md py-6'
    }`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between">
          <Link to="/preview" className="flex items-center space-x-3">
            <img src="/spire-logo.png" alt="Spire Group" className="h-10" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-2">
            <Link to="/preview" className={`nav-link-minimal ${isActive('') || isActive('/') ? 'text-spire-purple' : ''}`}>Home</Link>
            <Link to="/preview/our-story" className={`nav-link-minimal ${isActive('/our-story') ? 'text-spire-purple' : ''}`}>Our Story</Link>
            <Link to="/preview/services" className={`nav-link-minimal ${isActive('/services') ? 'text-spire-purple' : ''}`}>Services</Link>
            <Link to="/preview/our-method" className={`nav-link-minimal ${isActive('/our-method') ? 'text-spire-purple' : ''}`}>Our Method</Link>
            <Link to="/preview/team" className={`nav-link-minimal ${isActive('/team') ? 'text-spire-purple' : ''}`}>Team</Link>
            <Link to="/preview/contact" className="ml-4 btn-spire-primary">Contact Us</Link>
          </div>

          {/* Mobile Menu Button */}
          <button className="md:hidden text-gray-900" onClick={() => setIsMenuOpen(!isMenuOpen)}>
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden py-4 animate-fade-in-up">
            <div className="flex flex-col space-y-2">
              <Link to="/preview" className="nav-link-minimal block" onClick={() => setIsMenuOpen(false)}>Home</Link>
              <Link to="/preview/our-story" className="nav-link-minimal block" onClick={() => setIsMenuOpen(false)}>Our Story</Link>
              <Link to="/preview/services" className="nav-link-minimal block" onClick={() => setIsMenuOpen(false)}>Services</Link>
              <Link to="/preview/our-method" className="nav-link-minimal block" onClick={() => setIsMenuOpen(false)}>Our Method</Link>
              <Link to="/preview/team" className="nav-link-minimal block" onClick={() => setIsMenuOpen(false)}>Team</Link>
              <Link to="/preview/contact" className="btn-spire-primary mt-4 text-center" onClick={() => setIsMenuOpen(false)}>Contact Us</Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

const PreviewFooter = () => {
  return (
    <footer className="bg-gray-900 text-white py-16">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid md:grid-cols-4 gap-12">
          <div className="md:col-span-2">
            <img src="/spire-logo.png" alt="Spire Group" className="h-12 mb-6" />
            <p className="text-gray-400 text-base leading-relaxed mb-6 max-w-md">
              Leading healthcare real estate consulting firm in North America.
            </p>
          </div>
          
          <div>
            <h4 className="font-bold text-lg mb-4">Quick Links</h4>
            <ul className="space-y-3">
              <Link to="/preview/our-story" className="text-gray-400 hover:text-white transition block">Our Story</Link>
              <Link to="/preview/services" className="text-gray-400 hover:text-white transition block">Services</Link>
              <Link to="/preview/our-method" className="text-gray-400 hover:text-white transition block">Our Method</Link>
            </ul>
          </div>
          
          <div>
            <h4 className="font-bold text-lg mb-4">Contact</h4>
            <ul className="space-y-3 text-gray-400">
              <li>info@spiregrp.com</li>
            </ul>
          </div>
        </div>
        
        <div className="border-t border-gray-800 mt-12 pt-8 text-center text-gray-500">
          <p>&copy; 2025 Spire Group Inc. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

const PreviewSite = () => {
  return (
    <div className="preview-site">
      <PreviewNavigation />
      
      {/* Add padding for fixed nav */}
      <div className="pt-20">
        <Routes>
          <Route path="/" element={<PreviewHome />} />
          <Route path="/our-story" element={<PreviewOurStory />} />
          <Route path="/services" element={<PreviewServices />} />
          <Route path="/our-method" element={<PreviewOurMethod />} />
          <Route path="/team" element={<PreviewTeam />} />
          <Route path="/contact" element={<PreviewContact />} />
          <Route path="/image-picker" element={<ImagePicker />} />
        </Routes>
      </div>
      
      <PreviewFooter />
      
      {/* Preview Mode Badge */}
      <div className="fixed bottom-4 right-4 bg-yellow-500 text-black px-6 py-3 rounded-full font-bold shadow-2xl z-50 animate-pulse-slow">
        🎨 PREVIEW SITE
      </div>
      
      {/* Back to Production Link */}
      <div className="fixed bottom-4 left-4 z-50">
        <Link 
          to="/" 
          className="bg-white text-gray-900 px-6 py-3 rounded-full font-semibold shadow-2xl hover:shadow-3xl transition-all hover:scale-105"
        >
          ← Back to Live Site
        </Link>
      </div>
    </div>
  );
};

export default PreviewSite;